import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Loader } from '@googlemaps/js-api-loader';
import { FormShell } from '../onyx/FormShell';
import { FormActions } from '../onyx/Field';
import { Button } from '../onyx/Button';
import { getOnboardingForm, submitOnboardingForm } from '../../services/backend.service';
import QuestionRenderer from './QuestionRenderer';
import {
  buildValuesForSubmit,
  isPathComplete,
  visibleFormQuestions,
} from './formBranch';

function emptyAddressValue() {
  return {
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
  };
}

function normalizeDraftValues(values, questions) {
  const next = { ...(values || {}) };
  (questions || []).forEach((q) => {
    const tag = (q.semantic_tag || '').trim();
    if (!tag) return;
    if (q.question_type === 'address' && (next[tag] == null || typeof next[tag] !== 'object')) {
      next[tag] = emptyAddressValue();
    }
  });
  return next;
}

/**
 * Schema-driven onboarding form (Preferences / Updates).
 * Uses FormShell + compact Field / ChoiceGroup — same DS surface as #846 preferences.
 */
export function OnboardingForm({
  stepKey,
  onComplete,
  className = 'preferences-form',
  submitLabel = 'Continue',
  allowSkip = false,
  disclaimer = null,
  presentation = 'field',
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [questions, setQuestions] = useState([]);
  const [values, setValues] = useState({});
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const isSubmittingRef = useRef(false);

  const loadForm = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const { data, error: apiError } = await getOnboardingForm(token, stepKey);
      if (apiError) {
        throw new Error(apiError.message || String(apiError));
      }
      setTitle(data?.title || '');
      setIntro(data?.intro || '');
      setQuestions(data?.questions || []);
      setValues(normalizeDraftValues(data?.values, data?.questions));
    } catch (err) {
      console.error('Failed to load onboarding form:', err);
      setError('Failed to load this form. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, stepKey]);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  useEffect(() => {
    const loadGoogleMaps = async () => {
      try {
        const loader = new Loader({
          apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
          version: 'weekly',
          libraries: ['places'],
        });
        await loader.load();
        setMapsLoaded(true);
      } catch (err) {
        console.error('Error loading Google Maps:', err);
      }
    };
    loadGoogleMaps();
  }, []);

  const visibleQuestions = useMemo(
    () => visibleFormQuestions(questions, values),
    [questions, values],
  );

  const canSubmit = useMemo(
    () => isPathComplete(questions, values),
    [questions, values],
  );

  const handleValueChange = (tag, nextValue) => {
    setValues((prev) => ({
      ...prev,
      [tag]: nextValue,
    }));
  };

  const finishSubmit = async (payloadBuilder) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const { data, error: apiError } = await payloadBuilder(token);
      if (apiError) {
        throw new Error(apiError.message || String(apiError));
      }
      if (data?.values) {
        setValues(normalizeDraftValues(data.values, questions));
      }
      if (onComplete) {
        await onComplete(stepKey);
      }
    } catch (err) {
      console.error('Onboarding form submit failed:', err);
      setError(err.message || 'Failed to save. Please try again.');
      isSubmittingRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      setError('Please answer all required questions.');
      return;
    }
    await finishSubmit((token) => {
      const payload = buildValuesForSubmit(questions, values);
      return submitOnboardingForm(token, stepKey, payload);
    });
  };

  const handleSkip = async () => {
    await finishSubmit((token) => submitOnboardingForm(token, stepKey, {}, { skip: true }));
  };

  if (loading) {
    return (
      <FormShell className={className} actionsAlign="center">
        <p className="content__body">Loading…</p>
      </FormShell>
    );
  }

  return (
    <FormShell
      className={className}
      actionsAlign="center"
      data-testid={`onboarding-form-${stepKey}`}
    >
      {title ? (
        <h1 id="page-title" className="content__title">
          {title}
        </h1>
      ) : null}
      {intro ? <p className="content__body">{intro}</p> : null}

      {error ? (
        <div className="error-message" role="alert">
          {error}
        </div>
      ) : null}

      <div className="preferences-fields onboarding-form-questions">
        {visibleQuestions.map((question) => {
          const tag = (question.semantic_tag || '').trim();
          const value = tag ? values[tag] : undefined;
          return (
            <QuestionRenderer
              key={question.id}
              question={question}
              value={value}
              mapsLoaded={mapsLoaded}
              presentation={presentation}
              onChange={(next) => {
                if (tag) handleValueChange(tag, next);
              }}
            />
          );
        })}
      </div>

      {disclaimer ? (
        <p className="onyx-prose-muted onboarding-form-disclaimer" data-testid="onboarding-form-disclaimer">
          {disclaimer}
        </p>
      ) : null}

      <FormActions align="center" className="preferences-form-actions">
        <Button
          type="button"
          variant="primary"
          onClick={handleSubmit}
          disabled={submitting || !canSubmit}
        >
          {submitting ? 'Saving…' : submitLabel}
        </Button>
        {allowSkip ? (
          <Button
            type="button"
            variant="ghost"
            className="onboarding-form-skip"
            onClick={handleSkip}
            disabled={submitting}
          >
            Skip
          </Button>
        ) : null}
      </FormActions>
    </FormShell>
  );
}
